/**
 * ============================================================================
 * 🧭 Multi-Domain Telemetry Inspector (Aircraft & Marine Vessels)
 * ============================================================================
 */

import { renderPhotoDots } from '../utils/photoDots.js';

export class InspectorController {
  constructor(apiService, options = {}) {
    this.api = apiService;
    this.onExpandPhoto = options.onExpandPhoto || null;
    this.onClose = options.onClose || null;

    this.selectedItem = null;
    this.photos = [];
    this.currentPhotoIndex = 0;
    this.profileRoute = null;

    // Elements
    this.panelEl = document.getElementById('floating-inspector');
    this.closeBtn = document.getElementById('inspector-close-btn');
    this.heroWrapper = document.getElementById('hero-photo-wrapper');
    this.heroImg = document.getElementById('hero-photo-img');
    this.heroPlaceholder = document.getElementById('hero-photo-placeholder');
    this.dotsContainer = document.getElementById('photo-dots-nav');
    this.aircraftTypeEl = document.getElementById('ins-aircraft-type');
    this.statsGridEl = document.getElementById('ins-stats-grid');

    this.init();
  }

  init() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hide();
        if (this.onClose) this.onClose();
      });
    }

    if (this.heroWrapper) {
      this.heroWrapper.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.photos.length > 0 && this.onExpandPhoto) {
          this.onExpandPhoto(this.photos, this.currentPhotoIndex, this.selectedItem);
        }
      });
    }

    if (this.heroImg) {
      this.heroImg.addEventListener('error', () => {
        this.showPlaceholder();
      });
    }
  }

  show(item) {
    this.selectedItem = item;
    this.photos = [];
    this.currentPhotoIndex = 0;
    this.profileRoute = null;
    if (this.aircraftTypeEl) this.aircraftTypeEl.textContent = '';
    if (this.statsGridEl) this.statsGridEl.innerHTML = '';

    this.showPlaceholder();
    renderPhotoDots(this.dotsContainer, 0, 0, null);

    this.renderInfo(item);
    this.loadPhotos(item);

    if (this.panelEl) this.panelEl.classList.add('open');
  }

  hide() {
    this.selectedItem = null;
    this.photos = [];
    this.profileRoute = null;

    if (this.panelEl) this.panelEl.classList.remove('open');
  }

  refresh(item) {
    if (!item || !this.selectedItem) return;
    this.selectedItem = item;
    this.renderInfo(item);
  }

  renderInfo(item) {
    const flightEl = document.getElementById('ins-flight-id');
    const dynamicEl = document.getElementById('ins-dynamic-telemetry');
    const routeEl = document.getElementById('ins-route-telemetry');
    const tagEl = document.querySelector('.inspector-tag');

    const isVessel = (item.category === 'vessel' || !item.registration);

    if (isVessel) {
      // 🚢 Marine Vessel Display
      if (tagEl) tagEl.innerHTML = `<span class="domain-badge-sea">🚢 MARITIME AIS</span> <span style="margin-left:4px;">VESSEL DETAIL</span>`;
      
      const shipName = item.name || `VESSEL-${item.mmsi}`;
      if (flightEl) flightEl.textContent = shipName;

      const typeThai = item.ship_type_thai || item.ship_type_name || 'เรือทั่วไป';
      if (this.aircraftTypeEl) this.aircraftTypeEl.textContent = `⚓ ${typeThai}`;

      const sog = item.sog_kts != null ? `${item.sog_kts} kts` : '0 kts';
      const cog = item.cog_deg != null ? `${item.cog_deg}°` : '0°';
      const hdg = item.true_heading != null ? `${item.true_heading}°` : cog;
      if (dynamicEl) dynamicEl.textContent = `SOG ${sog} · COG ${cog} · HDG ${hdg}`;

      const dest = item.destination && item.destination !== '-' ? item.destination : 'Unspecified';
      if (routeEl) routeEl.textContent = `🎯 ท่าเรือปลายทาง: ${dest}`;

      if (this.statsGridEl) {
        this.statsGridEl.innerHTML = `
          <div class="stat-item"><span class="stat-lbl">MMSI</span><span class="stat-val font-mono">${item.mmsi}</span></div>
          <div class="stat-item"><span class="stat-lbl">IMO</span><span class="stat-val font-mono">${item.imo || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">CALLSIGN</span><span class="stat-val font-mono">${item.callsign || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">STATUS</span><span class="stat-val" style="font-size:10px;">${item.nav_status_text || 'Active'}</span></div>
          <div class="stat-item"><span class="stat-lbl">DRAUGHT</span><span class="stat-val">${item.draught_m ? item.draught_m + ' m' : '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">DIMENSIONS</span><span class="stat-val">${item.length_m ? (item.length_m + '×' + item.width_m + 'm') : '-'}</span></div>
        `;
      }

    } else {
      // ✈️ Aircraft Display
      if (tagEl) tagEl.innerHTML = `<span class="domain-badge-air">✈️ AIRSPACE</span> <span style="margin-left:4px;">AIRCRAFT DETAIL</span>`;
      
      const callsign = item.callsign || item.registration || 'N/A';
      const flightNo = item.flight_number && item.flight_number !== callsign ? ` / ${item.flight_number}` : '';
      if (flightEl) flightEl.textContent = `${callsign}${flightNo}`;

      const alt = item.altitude_ft || '0 ft';
      const spd = item.ground_speed_kts || '0 kts';
      const hdg = item.track_deg || '0°';
      const vs = item.vertical_speed_fpm;
      const vsPart = vs && vs !== '0 fpm' ? ` · VS ${vs}` : '';
      if (dynamicEl) dynamicEl.textContent = `ALT ${alt} · SPD ${spd} · HDG ${hdg}${vsPart}`;

      if (this.aircraftTypeEl) {
        const airline = item.airline || '';
        const model = item.aircraft_model || item.aircraft_code || '';
        this.aircraftTypeEl.textContent = [airline, model].filter(Boolean).join(' · ') || 'Commercial Aircraft';
      }

      const orig = this.profileRoute?.origin || item.origin_airport || item.origin || '-';
      const dest = this.profileRoute?.destination || item.destination_airport || item.destination || '-';
      if (routeEl) routeEl.textContent = `${orig} → ${dest}`;

      if (this.statsGridEl) {
        this.statsGridEl.innerHTML = `
          <div class="stat-item"><span class="stat-lbl">REG</span><span class="stat-val font-mono">${item.registration || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">ICAO</span><span class="stat-val font-mono">${item.icao_hex || item.hex || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">AIRLINE</span><span class="stat-val">${item.airline || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">MODEL</span><span class="stat-val">${item.aircraft_model || item.aircraft_code || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">SQUAWK</span><span class="stat-val font-mono">${item.squawk || '-'}</span></div>
          <div class="stat-item"><span class="stat-lbl">PHASE</span><span class="stat-val">${item.flight_phase || '-'}</span></div>
        `;
      }
    }
  }

  async loadPhotos(item) {
    const isVessel = (item.category === 'vessel' || !item.registration);
    this.photos = [];

    if (isVessel) {
      // Load Vessel Photo
      const mmsi = item.mmsi;
      try {
        if (item.photo_url) {
          this.photos.push({
            url: item.photo_url,
            photographer: 'Verified VesselFinder CDN'
          });
          this.renderPhoto();
          return;
        }

        const res = await this.api.getMarineDetail(mmsi);
        if (res && res.vessel && res.vessel.photo_url) {
          this.photos.push({
            url: res.vessel.photo_url,
            photographer: 'Verified VesselFinder CDN'
          });
          this.renderPhoto();
        }
      } catch (e) {
        console.warn('[Inspector] Vessel photo lookup failed:', e);
      }

    } else {
      // Load Aircraft Photo
      const reg = item.registration;
      if (item.photo && item.photo.has_photo) {
        this.photos.push({
          url: this.api.getPhotoUrl(reg),
          photographer: 'Verified JetPhotos CDN'
        });
        this.renderPhoto();
      }

      try {
        const profile = await this.api.getAircraftProfile(reg);
        if (profile) {
          this.profileRoute = {
            origin: profile.origin?.iata || profile.origin?.name || '-',
            destination: profile.destination?.iata || profile.destination?.name || '-'
          };
          this.renderInfo(this.selectedItem);

          if (this.aircraftTypeEl && profile.aircraft?.model) {
            this.aircraftTypeEl.textContent = `${profile.airline || ''} · ${profile.aircraft.model}`.trim();
          }

          if (this.statsGridEl) {
            this.statsGridEl.innerHTML = `
              <div class="stat-item"><span class="stat-lbl">REG</span><span class="stat-val font-mono">${reg}</span></div>
              <div class="stat-item"><span class="stat-lbl">ICAO</span><span class="stat-val font-mono">${profile.aircraft?.icao_hex || '-'}</span></div>
              <div class="stat-item"><span class="stat-lbl">AIRLINE</span><span class="stat-val">${profile.airline || '-'}</span></div>
              <div class="stat-item"><span class="stat-lbl">MODEL</span><span class="stat-val">${profile.aircraft?.model || '-'}</span></div>
              <div class="stat-item"><span class="stat-lbl">SQUAWK</span><span class="stat-val font-mono">${item.squawk || '-'}</span></div>
              <div class="stat-item"><span class="stat-lbl">PHASE</span><span class="stat-val">${item.flight_phase || '-'}</span></div>
            `;
          }

          if (profile.photos) {
            let gallery = [];
            if (Array.isArray(profile.photos)) {
              gallery = profile.photos.map(p => ({
                url: p.url || this.api.getPhotoUrl(reg),
                photographer: p.photographer || 'Verified JetPhotos CDN'
              }));
            } else if (profile.photos.metadata && Array.isArray(profile.photos.metadata.photos) && profile.photos.metadata.photos.length > 0) {
              gallery = profile.photos.metadata.photos.map(p => ({
                url: `${this.api.baseUrl}/api/photos/${encodeURIComponent(reg)}/${encodeURIComponent(p.filename)}`,
                photographer: p.photographer || 'JetPhotos Contributor'
              }));
            } else if (Array.isArray(profile.photos.all_images) && profile.photos.all_images.length > 0) {
              gallery = profile.photos.all_images.map(imgUrl => ({
                url: imgUrl.startsWith('http') ? imgUrl : `${this.api.baseUrl}${imgUrl}`,
                photographer: profile.photos.photographer || 'Verified JetPhotos CDN'
              }));
            } else if (profile.photos.has_photo) {
              gallery = [{
                url: this.api.getPhotoUrl(reg),
                photographer: profile.photos.photographer || 'Verified JetPhotos CDN'
              }];
            }
            if (gallery.length > 0) {
              this.photos = gallery;
              this.currentPhotoIndex = 0;
              this.renderPhoto();
            }

            // On-demand fetch if fewer than 4 photos are cached
            if (gallery.length < 4 && !isVessel && reg) {
              this.api.fetchMorePhotos(reg).then(moreRes => {
                if (moreRes && Array.isArray(moreRes.all_images) && moreRes.all_images.length > this.photos.length) {
                  this.photos = moreRes.all_images.map(imgUrl => ({
                    url: imgUrl.startsWith('http') ? imgUrl : `${this.api.baseUrl}${imgUrl}`,
                    photographer: 'Verified JetPhotos CDN'
                  }));
                  this.renderPhoto();
                }
              }).catch(() => {});
            }
          }
        }
      } catch (e) {
        console.warn('[Inspector] Profile fetch failed:', e);
      }
    }
  }

  renderPhoto() {
    if (this.photos.length > 0) {
      const p = this.photos[this.currentPhotoIndex];
      if (this.heroImg) {
        this.heroImg.src = p.url;
        this.heroImg.style.display = 'block';
      }
      if (this.heroPlaceholder) this.heroPlaceholder.style.display = 'none';

      renderPhotoDots(this.dotsContainer, this.photos.length, this.currentPhotoIndex, (idx) => {
        this.currentPhotoIndex = idx;
        this.renderPhoto();
      });
    } else {
      this.showPlaceholder();
    }
  }

  showPlaceholder() {
    if (this.heroImg) {
      this.heroImg.style.display = 'none';
      this.heroImg.src = '';
    }
    if (this.heroPlaceholder) {
      this.heroPlaceholder.style.display = 'flex';
      const isVessel = this.selectedItem?.category === 'vessel';
      this.heroPlaceholder.innerHTML = isVessel ? `
        <span style="font-size:28px;">🚢</span>
        <span style="font-size:11px; color:#64748b; margin-top:4px;">NO VESSEL PHOTO</span>
      ` : `
        <span style="font-size:28px;">✈️</span>
        <span style="font-size:11px; color:#64748b; margin-top:4px;">NO AIRCRAFT PHOTO</span>
      `;
    }
    renderPhotoDots(this.dotsContainer, 0, 0, null);
  }
}
